<template>
	<Loader v-if="loading" />
		<!-- why do we have to do this in the body components -->
		<Page v-else>
    	<Breadcrumbs root-path="/" :showJumpTo="true" />
			<div class="page-content">
				<BatchHeader :batch="batch" />

				<div class="buttons-container">
					<PermissionsWrapper
						:permissionIds="[Permissions.DisbursementBatches]"
						:permissionLevel="PermissionLevels.Update"
					>
						<button :disabled="batch?.isApproved || isProcessing" @click="approveBatchAsync">
							Approve
						</button>
					</PermissionsWrapper>
					<button class="secondary" @click="goBack">Back</button>
				</div>

				<div class="table-wrapper">
					<table>
						<thead>
							<tr>
								<th v-for="header in headers" :key="header.key" :style="{ maxWidth: header?.maxWidth || 'none' }" @click="handleHeaderClick(header)">
									<span
											v-if="header.sortable && !header.customLabel"
											:class="isActiveSort(header.key)"
											:title="header.title"
										>
											{{ header.label }} {{ getSortArrow(header.key) }}
										</span>
										<div class="custom-header" v-else-if="header.sortable && header.customLabel" style="">
											<span class="label" :class="isActiveSort(header.key)">{{ header.customLabel.line1 }} <br /> {{ header.customLabel?.line2 }}</span>
											<span class="sort-arrow" >{{ header.sortArrow }}</span>
										</div>
										<span v-else>
											{{ header.label }}
										</span>
								</th>
							</tr>
						</thead>
						<tbody>
							<BatchDisbursementRow
								v-for="reference in sortedDisbursementBatchReferences"
								:key="reference.disbursementId"
								:batchId="batch?.id"
								:reference="reference"
								:disbursement="getReferenceDisbursement(reference.disbursementId)"
								:isBatchApproved="batch?.isApproved"
								:isProcessing="isProcessing"
								:onBatchUpdateAsync="fetchBatchDetailsAsync"
							/>
						</tbody>
					</table>
				</div>
			</div>
		</Page>
	
</template>

<script setup lang="ts">
	import { ref, onMounted, computed } from 'vue';
	import { useRouter, useRoute } from 'vue-router';
	import axios from 'axios';
	import Loader from '@/components/Loader.vue';
	import Page from "@/components/v2/layout/Page.vue";
	import functions from '@/use/functions';
	import { Permissions, PermissionLevels } from '@/models/permissions';
	import PermissionsWrapper from '@/components/wrappers/PermissionsWrapper.vue';
	import BatchHeader from './BatchHeader.vue';
	import BatchDisbursementRow from './BatchDisbursementRow.vue';
	import { DisbursementBatchDetailsModel, DisbursementBatchReferenceModel } from '@/models/disbursementBatchesV2';
	import Breadcrumbs from "@/components/shared/Breadcrumbs.vue";

	const route = useRoute();
	const router = useRouter();

	const batch = ref<DisbursementBatchDetailsModel | null>(null);

	const loading = ref(false);
	const isProcessing = ref(false);

	interface Header {
		label: string;
		sortable: boolean;
		key: string;
		sortArrow: string;
		title?: string;
		customLabel?: {
			line1?: string;
			line2?: string;
		};
		maxWidth?: string;
	}

	const headers = computed<Array<Header>>(() => [
		{ label: 'In|Out', sortable: true, key: 'isDeleted', sortArrow: getSortArrow('isDeleted') },
		{ label: 'Credit Union', sortable: true, key: 'lenderName', sortArrow: getSortArrow('lenderName')},
		{ label: 'Borrower (F/L)', sortable: true, key: 'borrowerName',   sortArrow: getSortArrow('borrowerName')},
		{ label: 'Sch Disb Amt', sortable: true, key: 'disbursementAmount', title: 'Scheduled Disbursement Amount', sortArrow: getSortArrow('disbursementAmount')},
		{ label: 'Disb Method', sortable: true, key: 'disbursementMethodHumanized', title: 'Disbursement Method', sortArrow: getSortArrow('disbursementMethodHumanized')},
		{ label: 'Disb Date', sortable: true, key: 'disbursementDateFormatted', sortArrow: getSortArrow('disbursementDateFormatted')},
		{ label: 'Reference', sortable: true, key: 'referenceId', sortArrow: getSortArrow('referenceId')},
		{ label: 'Lender Use (LoanID + DisbID)', customLabel: { line1: 'Lender Use', line2: '(LoanID + DisbID)'}, sortable: true, key: 'lenderUseOnly', maxWidth: '110px', sortArrow: getSortArrow('lenderUseOnly')},
		{ label: 'Disb Seq #', sortable: true, key: 'sequenceNumber', title: 'Disbursement Sequence Number', sortArrow: getSortArrow('sequenceNumber')},
	]);

	const getReferenceDisbursement = (disbursementId: number) => batch.value?.disbursements.find(d => d.id == disbursementId);

	const fetchBatchDetailsAsync = async () => {
		try {
			const response = await axios.get(`/api/v2/disbursement-batches/${route.params.batchId}`);

			batch.value = response.data;
		} catch (error) {
			console.error(error);

			functions.openModal({
				title: 'Oops!',
				description: `Failed to get details for disbursement batch ${route.params.batchId}. Please try again later.`
			});
		} finally {
			loading.value = false;
		}
	};

	const approveBatchAsync = async () => {
		if (isProcessing.value) {
			return;
		}

		isProcessing.value = true;
		loading.value = true;

		try {
			await axios.post(`/api/v2/disbursement-batches/${route.params.batchId}/approve`);
			await fetchBatchDetailsAsync();
		} catch (error) {
			console.error(error);

			functions.openModal({
				title: 'Oops!',
				description: `Failed to approve disbursement batch ${route.params.batchId}. Please try again later.`
			});
		} finally {
			isProcessing.value = false;
			loading.value = false;
		}
	};

	const goBack = () => router.go(-1);

	const sortKey = ref('');
	const sortOrder = ref(1);

	const sortedDisbursementBatchReferences = computed<DisbursementBatchReferenceModel[]>(() => {
		if (!batch.value) {
			return [];
		} else if (!sortKey.value) {
			return batch.value.disbursementBatchReferences; // no sorting, return original order
		} else if (sortKey.value === 'isDeleted') {
			return sortByInOut([...batch.value.disbursementBatchReferences], sortKey.value)
		}

		return sortByProperty([...batch.value.disbursementBatchReferences], sortKey.value);
	});

	const sortBy = (key: string) => {
		if (sortKey.value === key) {
			if (sortOrder.value === 1) {
				sortOrder.value = -1; // second click, descending
			} else if (sortOrder.value === -1) {
				sortKey.value = ''; // third click, reset sort
				sortOrder.value = 1; // default sort order for the next click
			}
		} else {
			sortKey.value = key;
			sortOrder.value = 1; // first click, ascending
		}
	};

	const getSortArrow = (key: string) => {
		if (sortKey.value === key) {
			return sortOrder.value === 1 ? '▲' : sortOrder.value === -1 ? '▼' : ' ';
		}
		return '';
	};

	const isActiveSort = (key: string) => (sortKey.value === key ? 'active-sort' : '');

	const sortByInOut = (arr, property: string) => {
		return arr.sort((a, b)=> {
			return sortOrder.value === 1 ? Number(b[property]) - Number(a[property]) :Number(a[property]) - Number(b[property]) ;
		});
	} 
	
	const handleHeaderClick = (header: Header) => {
		if (header.sortable) {
			sortBy(header.key)
		}
	}

	function sortByProperty(
		arr: DisbursementBatchReferenceModel[],
		property: string,
	): DisbursementBatchReferenceModel[] {
		return [...arr].sort((a, b) => {		
			const disA = batch.value!.disbursements.find(d => d.id === a.disbursementId);
    	const disB = batch.value!.disbursements.find(d => d.id === b.disbursementId);

			let valA = disA ? disA[property] : undefined;
			let valB = disB ? disB[property] : undefined;
			
			// Sorts by property with undefined first when ASC
			if (sortOrder.value === 1) {
				if (valA === undefined && valB !== undefined) return -1;
				if (valB === undefined && valA !== undefined) return 1;
			}

			// Sorts by property with undefined last when DESC
			if (sortOrder.value === -1) {
				if (valA === undefined && valB !== undefined) return 1;
				if (valB === undefined && valA !== undefined) return -1;
			}

			
			// Fallback for when both are defined or both are undefined.
			// For string properties, use localeCompare(). For numbers, use subtraction.
			// Uses localeCompare for natural sorting, considering numbers within strings
			if (typeof valA === 'string' && typeof valB === 'string') {
				valA = valA.toLowerCase();
				valB = valB.toLowerCase();

				switch (sortOrder.value) {
					case 1: return valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
					case -1: return valB.localeCompare(valA, undefined, { numeric: true, sensitivity: 'base' });
					default: return 0;
				}
			}

			if (typeof valA === 'number' && typeof valB === 'number') {
				switch (sortOrder.value) {
					case 1: return  valA - valB ;
					case -1: return valB - valA;
					default: return 0;
				}
			}

			// For other types, or if both are undefined, preserve original order.
			return 0;
		});
	}

	onMounted(async () => {
		loading.value = true;

		try {
			await fetchBatchDetailsAsync();
		} catch (error) {
			console.error(error);
		} finally {
			loading.value = false;
		}
	});
</script>

<style scoped>
	.page-content {
		padding: 0 20px;
		font-family: "Montserrat", arial, sans-serif;
		margin-top: 20px;
	}

	.table-wrapper {
		overflow-x: auto;
		width: 100%;
	}

	table {
		width: 100%;
		border-collapse: collapse;
		font-size: 14px;
		margin-top: 20px;
		margin-bottom: 20px;
		text-align: center;
		border: 1px solid #ccc;
		position: relative;
	}

	table tr:hover {
		background-color: #f2f2f2;
		transition: background-color 0.2s;
	}

	thead th {
		background-color: #f2f2f2;
		font-weight: 700;
		padding: 10px 5px;
		border-bottom: 1px solid #ccc;
		cursor: pointer;
		user-select: none;
	}

	th:hover {
		background-color: #ddd;
	}

	tr:nth-child(even) {
		background-color: #f9f9f9;
	}

	td,
	th {
		text-align: center;
	}

	td:nth-child(1) {
		width: 40px;
	}

	.buttons-container {
		display: flex;
		justify-content: flex-end;
		margin-top: 15px;
		gap: 10px;
	}

	button {
		padding: 8px 20px;
		font-size: 14px;
		font-weight: bold;
		background-color: black;
		color: white;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		transition: background-color 0.2s, color 0.2s, opacity 0.2s;
	}

	button:hover {
		background-color: #333;
		cursor: pointer;
	}

	button.secondary {
		color: black;
		background-color: white;
		box-shadow: inset 0 0 0 2px #ccc;
	}

	button.secondary:hover {
		color: white;
		background-color: black;
		box-shadow: inset 0 0 0 2px black;
	}

	button:disabled {
		background-color: #ccc;
		color: #666;
		cursor: not-allowed;
		opacity: 0.7;
	}

	button.secondary:disabled {
		background-color: #ccc;
		color: #999;
		box-shadow: inset 0 0 0 2px #ccc;
	}

	.custom-header {
		display: flex;
		justify-content: center;
		flex-wrap: wrap;
	}

	.custom-header .label {
		flex: 1;
	}

	.custom-header .sort-arrow {
		display: flex; 
		flex: 0 0 1; 
		align-items: center; 
		justify-content: center;
	}

</style>
